import { startTransition } from 'react';

import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations.ts';
import { createDataFetcherForSuspense } from '@confluence/query-preloader-tools/entry-points/dataFetcherForSuspense.ts';

import { getInitialState } from './getInitialState.ts';
import type { SmartLinkType } from './getSmartLinks.ts';
import {
	preloadContentSmartlinksQuery,
	fetchORSForSSRWithTimeout,
	SMART_LINK_INDIVIDUAL_TIMEOUT,
	SMART_LINK_BATCH_TIMEOUT,
	DEFAULT_MIN_BATCH_COUNT,
	DEFAULT_MAX_BATCH_COUNT,
} from './preloadContentSmartLinks.ts';
import {
	groupSmartLinksByHostname,
	mergeSmallThirdPartyBatches,
	splitLinksIntoBatches,
	isFirstPartyLink,
} from './smartLinkBatchHelpers.ts';

/*
 * Statsig timeout configs for preloading with suspense
 */
function getStreamingTimeoutsAndBatchSizes() {
	// How long a single ORS resolution can take
	const timeout = StatsigConfigurations.getNumber(
		'confluence_smart_link_streaming_max_time',
		SMART_LINK_INDIVIDUAL_TIMEOUT,
	);

	// How long a batch of ORS resolution can take
	const batchTimeout = StatsigConfigurations.getNumber(
		'confluence_smart_link_streaming_batch_max_time',
		SMART_LINK_BATCH_TIMEOUT,
	);

	// How many links should be packed in a batch
	const min = StatsigConfigurations.getNumber(
		'confluence_smart_link_min_batch_count',
		DEFAULT_MIN_BATCH_COUNT,
	);

	const max = Math.max(
		min,
		StatsigConfigurations.getNumber(
			'confluence_smart_link_streaming_max_batch_count',
			DEFAULT_MAX_BATCH_COUNT,
		),
	);

	return { timeout, batchTimeout, min, max };
}

async function resolveViaORSInBatches(
	links: SmartLinkType[],
	timeout: number,
	batchTimeout: number,
	min: number,
	max: number,
	isThirdParty?: boolean,
): Promise<any[]> {
	if (!links.length) {
		return [];
	}

	const fetchTimeout: Record<string, number> = {};

	// mirroring batching logic in preloadContentSmartLinks.ts
	let smartLinksBatches = groupSmartLinksByHostname(links);
	// only doing 3p split in the case where we know link is 3p
	if (isThirdParty) {
		smartLinksBatches = mergeSmallThirdPartyBatches(smartLinksBatches, min);
	}
	smartLinksBatches = splitLinksIntoBatches(smartLinksBatches, max);

	const orsCalls: Promise<any>[] = smartLinksBatches.map((batch) =>
		fetchORSForSSRWithTimeout(batch, timeout, batchTimeout, fetchTimeout),
	);

	// eslint-disable-next-line check-preloaders/no-promise-all
	return (await Promise.all(orsCalls)).flat();
}

// merge links when batches resolve into global window.__SSR_SMARTLINKS__
function mergeIntoWindowSSRSmartlinks(resolvedSubset: any[]) {
	const subsetState = getInitialState(resolvedSubset);
	window.__SSR_SMARTLINKS__ = {
		...(window.__SSR_SMARTLINKS__ || {}),
		...subsetState,
	};
}

type SmartLinksBucket = {
	name: string;
	filter: (link: SmartLinkType) => boolean;
	isThirdParty: boolean;
};
/**
 * Given a shared allLinksPromise and a bucket definition (either first or third party),
 * filter, batch via ORS, and merge into window.__SSR_SMARTLINKS__.
 */
async function preloadSmartLinksBucketForSuspense(
	allLinksPromise: Promise<SmartLinkType[]>,
	bucket: SmartLinksBucket,
) {
	const allLinks = await allLinksPromise;
	const subset = allLinks.filter(bucket.filter);
	if (!subset.length) {
		return;
	}
	const { timeout, batchTimeout, min, max } = getStreamingTimeoutsAndBatchSizes();
	const resolved = await resolveViaORSInBatches(
		subset,
		timeout,
		batchTimeout,
		min,
		max,
		bucket.isThirdParty,
	);
	startTransition(() => {
		mergeIntoWindowSSRSmartlinks(resolved);
	});
	return resolved;
}

export function preloadContentSmartLinksWithSuspense(contentId: string, isDraft?: boolean) {
	const allLinksPromise = preloadContentSmartlinksQuery(contentId, isDraft);
	const firstPartyBucket = {
		name: 'first-party',
		filter: (link: SmartLinkType) => isFirstPartyLink(link.resourceUrl),
		isThirdParty: false,
	} as const;
	const thirdPartyBucket = {
		name: 'third-party',
		filter: (link: SmartLinkType) => !isFirstPartyLink(link.resourceUrl),
		isThirdParty: true,
	} as const;
	createDataFetcherForSuspense('content-smartlinks-first-party', () =>
		preloadSmartLinksBucketForSuspense(allLinksPromise, firstPartyBucket),
	);
	createDataFetcherForSuspense('content-smartlinks-third-party', () =>
		preloadSmartLinksBucketForSuspense(allLinksPromise, thirdPartyBucket),
	);
}
